// Money — SPEC-001 §12, §14; Phase 5 read surface. Ground truth for LOOK and
// CONTENT: ~/nvManager-files/workspace/board/modules/page_money.gs (635
// lines) and its stylesheet boardCss_() at Code.gs:698 — same classes this
// client's client/src/styles/global.css already ports: `.section-head`,
// `.locations`/`.loc` (clinic overview cards), `.queue`/`.q`/`.btn` (needs-
// attention rows, same shape as Home.tsx's queue), `.table-wrap`/`table`,
// and `.tk`/`.meta`/`.state` (first-cell label+detail and a two-line status
// cell, same shape as Tasks.tsx's `td.state`).
//
// Reads-only from the normalized store (store.ts): this page never calls
// fetch (INV-1 — navigation between pages costs zero network calls). Money
// data is loaded once at sign-in by client/src/lib/bootstrap.ts, which this
// task may not edit (another agent — the lead — owns that wiring). This page
// defines the CONTRACT it expects and renders a named, visible empty/fault
// state until something populates it (INV-9), exactly like Reports.tsx and
// Tasks.tsx before it:
//
//   state.value.deposit      = { [id]: DepositRow }      — src/money.js
//     GET /api/v1/money/deposits `deposits[]`.
//   state.value.drawerCount  = { [id]: DrawerCountRow }  — GET
//     /api/v1/money/drawer-counts `drawerCounts[]`.
//   state.value.pettyCash    = { [id]: PettyCashRow }    — GET
//     /api/v1/money/petty-cash `pettyCash[]`.
//   state.value.vendor       = { [id]: VendorRow }       — GET
//     /api/v1/money/vendors `vendors[]`.
//   state.value.amexReceipt  = { [id]: AmexReceiptRow }  — GET
//     /api/v1/money/amex `amexReceipts[]`.
//   state.value.checkPayment = { [id]: CheckPaymentRow } — GET
//     /api/v1/money/checks `checkPayments[]`.
//   state.value.chargeback   = { [id]: ChargebackRow }   — GET
//     /api/v1/money/chargebacks `chargebacks[]`.
//
// No `moneySummary` slice: src/money.js's GET /api/v1/money/summary is a
// server-side roll-up of exactly these seven tables (see its module header),
// so this page computes the same per-clinic totals client-side from the raw
// rows above — the same discipline Home.tsx's `summarise()` already uses
// for tasks/work-items, and it keeps the store contract to the entity tables
// this task's other pages already establish rather than adding an eighth,
// oddly-shaped slice keyed by clinic instead of id.
//
// R-11 governs every render of a chargeback below: `status ===
// 'BLOCKED_NO_AMOUNT'` (amount is null) renders a NAMED "Blocked" state —
// never `$0.00`, never blank — and is never merged into a money total.
//
// HONEST GAPS — things page_money.gs's ground truth shows that today's API
// (src/money.js) cannot supply, named rather than silently dropped:
//   - No receipts. Ground truth's Deposits/PettyCash rows carry a
//     `receipt_url` (and write endpoints to attach/remove one);
//     src/money.js's listDeposits/listPettyCash select no such column, so
//     there is no receipt link to render here at all.
//   - No writes. mn_addDeposit/mn_addDrawerCount/mn_addPettyCash/
//     mn_deleteEntry/mn_attachFile/mn_removeAttachment have no REST
//     counterpart yet — this page is read-only, same file-scope limit as
//     Tasks.tsx (no client/src/lib/ API-calling module may be added here).
//   - Different Petty cash shape. Ground truth's PettyCash tab is
//     payee + amount (always an outflow); the migrated schema's petty_cash
//     table is reason + amount + a direction of IN or OUT, so "payee" is
//     rendered here as `reason` and there is a direction column that has no
//     ground-truth analogue.
//   - Different Deposits shape. Ground truth's Deposits tab has a free-text
//     `register` (which drawer); the migrated schema has no register column
//     at all, only `method` and a `matchStatus` against a bank statement
//     line that the Sheets-era app never tracked.
//   - Cash drawer notes are gone. Ground truth's DrawerCounts carried a free
//     `note` field; drawer_count in 009_money.sql has no note column.
import type { JSX } from 'preact';
import { state } from '../lib/store';
import type { PettyReceipt } from '../lib/pettyReceipts';
import { orderLocations, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { formatDate } from '../lib/date';
import { FaultState, EmptyState } from '../components/FaultState';
import {
  AddPettyCashForm, AddOtherCashItemForm, DeleteMoneyRowButton, PettyReceipts,
} from '../components/MoneyForms';
import { ClinicTabs, visibleClinics, activeClinicFrom, MANAGEMENT_TAB_ID, canSeeManagementTab } from '../components/ClinicTabs';
import { ManagementPlaceholder, SHOW_EMPTY_MANAGEMENT_TABS } from '../components/ManagementPlaceholder';
import { FavoriteStar } from '../components/FavoriteStar';
import { useEffect, useState } from 'preact/hooks';
import { useTableSort } from '../lib/tableSort';
import { SortableTh } from '../components/SortableTh';
import { DepositReconGrid } from '../components/DepositReconGrid';
import { useResource } from '../lib/store';
import { refreshDepositRecon, moneyShowOlder, showOlderMoney, moneyHistoryFrom } from '../lib/bootstrap';
import { authedGet } from '../lib/apiClient';
import { OpenFolderLink } from '../components/OpenFolderLink';

// --- store contract types ----------------------------------------------------

export interface DepositRow {
  id: number;
  locationCode: string;
  depositDate: string;
  amount: string | number;
  method: string | null;
  bankLineId: number | string | null;
  matchStatus: string | null;
  recordedBy: string | null;
  createdAt: string;
}

export interface DepositReconFile {
  id: number;
  url: string | null;
  name: string | null;
}

export interface DepositReconRow {
  id: number;
  locationCode: string;
  reconDate: string;
  mr080: string | null;
  envelope: string | null;
  overUnder: string | number | null;
  batchAmount: string | number | null;
  mr080Batch: string | number | null;
  batchOverUnder: string | number | null;
  notes: string | null;
  receipt?: DepositReconFile | null;
  envelopeFile?: DepositReconFile | null;
  recordedBy: string | null;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface DepositBatchRow {
  id: number;
  locationCode: string;
  dateStart: string;
  dateEnd: string;
  mr080Amount: string | number | null;
  batchAmount: string | number | null;
  depositAmount?: string | number | null;
  overUnder: string | number | null;
  notes: string | null;
  closedAt: string | null;
  closedBy: string | null;
  files?: DepositReconFile[];
  receipt?: DepositReconFile | null;
  envelopeFile?: DepositReconFile | null;
  recordedBy: string | null;
  createdAt: string;
  updatedAt?: string;
  updatedBy?: string | null;
  deletedAt?: string | null;
}

export interface DrawerCountRow {
  id: number;
  locationCode: string;
  countDate: string;
  countedAmount: string | number;
  expectedAmount: string | number;
  variance: string | number;
  countedBy: string | null;
  createdAt: string;
}

export interface PettyCashRow {
  id: number;
  locationCode: string;
  transactionDate: string;
  direction: 'IN' | 'OUT' | string;
  amount: string | number;
  reason: string | null;
  recordedBy: string | null;
  createdAt: string;
  /** Receipt photos/PDFs (idea 38). Absent on a row saved before the list loads. */
  receipts?: PettyReceipt[];
}

export interface VendorRow {
  id: number;
  name: string;
  active: boolean;
  createdAt: string;
}

export interface AmexReceiptRow {
  id: number;
  locationCode: string;
  transactionDate: string;
  amount: string | number;
  vendorId: number | string | null;
  statementLineId: number | string | null;
  matchStatus: string | null;
  recordedBy: string | null;
  createdAt: string;
}

export interface CheckPaymentRow {
  id: number;
  locationCode: string;
  checkNumber: string | null;
  payee: string | null;
  amount: string | number;
  issuedDate: string;
  memo: string | null;
  status: string | null;
  bankLineId: number | string | null;
  matchStatus: string | null;
  issuedBy: string | null;
  createdAt: string;
}

export interface OtherCashItemRow {
  id: number;
  locationCode: string;
  itemDate: string;
  direction: 'IN' | 'OUT' | string;
  amount: string | number;
  description: string | null;
  recordedBy: string | null;
  createdAt: string;
  deleted?: boolean;
}

export interface ChargebackRow {
  id: number;
  locationCode: string;
  occurredDate: string;
  // Never defaulted: null means BLOCKED_NO_AMOUNT (R-11) and must stay null
  // all the way through to render — never coerced to 0.
  amount: string | number | null;
  status: string;
  reason: string | null;
  createdAt: string;
}

function getTable<T>(entityType: string): Record<string, T> {
  return (state.value[entityType] ?? {}) as Record<string, T>;
}

// --- formatting helpers -------------------------------------------------------

/** '$12.34' / '-$12.34' / '—' for null/undefined — the exact digits the API
 * sent, never rounded or re-parsed through Number(). */
export function formatMoney(raw: string | number | null | undefined): string {
  if (raw === null || raw === undefined) return '—';
  const s = String(raw);
  return s.startsWith('-') ? `-$${s.slice(1)}` : `$${s}`;
}

/** Group rows by clinic and return them in canonical clinic order (INV-14) —
 * orderLocations is the ONLY function allowed to determine that order, so
 * this never sorts locations itself; it only sorts each clinic's own rows
 * by date, newest first. */
function groupedByLocation<T extends { locationCode: string }>(rows: T[], dateKey: keyof T): T[] {
  const byLoc = new Map<string, T[]>();
  for (const r of rows) {
    const list = byLoc.get(r.locationCode) ?? [];
    list.push(r);
    byLoc.set(r.locationCode, list);
  }
  for (const list of byLoc.values()) {
    list.sort((a, b) => String(b[dateKey] ?? '').localeCompare(String(a[dateKey] ?? '')));
  }
  const order = orderLocations([...byLoc.keys()] as LocationCode[]);
  return order.flatMap((code) => byLoc.get(code) ?? []);
}

function locationName(code: string): string {
  return (LOCATION_NAMES as Record<string, string>)[code] ?? code;
}

export const MONEY_MENU = [
  { id: 'deposits', label: 'Deposits' },
  { id: 'petty', label: 'Petty Cash' },
  { id: 'other', label: 'Other cash items' },
] as const;

export type MoneyMenuId = (typeof MONEY_MENU)[number]['id'];

// --- the page -----------------------------------------------------------------

function menuFromHash(): MoneyMenuId | null {
  const id = (window.location.hash || '').slice(1);
  return MONEY_MENU.some((m) => m.id === id) ? (id as MoneyMenuId) : null;
}

/**
 * Money is a tab inside Company now (owner, 2026-10-06: "Id like to move
 * reports and Money under company as a sub menu item"). Company passes
 * `embeddedClinic` — the clinic picked on Company's own strip — and the page
 * drops its own frame (`.main`, the "Money" heading, its clinic strip) so the
 * tab does not show a second header. Without the prop it is the standalone
 * page it always was. A `#deposits` / `#petty` / `#other` link (Quick Docs
 * pins) opens that menu item.
 */
export function MoneyPage({ embeddedClinic }: { embeddedClinic?: LocationCode } = {}): JSX.Element {
  const embedded = embeddedClinic !== undefined;
  const [clinic, setClinic] = useState<string | null>(null);
  const [management, setManagement] = useState(false);
  const showManagementTab = !embedded && SHOW_EMPTY_MANAGEMENT_TABS && canSeeManagementTab();
  const [menu, setMenu] = useState<MoneyMenuId>(() => menuFromHash() ?? 'deposits');
  // Owner rule 2026-09-10: every table has Add and Edit. Petty Cash and
  // Other Cash Items each get one row of editing state — set by the row's
  // Edit button, cleared by the form's Cancel or by a successful save
  // (updatePettyCash/updateOtherCashItem already fold the saved row into the
  // store via upsertEntity, so there is nothing else to "reload" here).
  const [editingPetty, setEditingPetty] = useState<PettyCashRow | null>(null);
  const [editingOther, setEditingOther] = useState<OtherCashItemRow | null>(null);
  const [olderState, setOlderState] = useState<'idle' | 'loading' | string>('idle');
  useResource('deposit-recon-year:v2', async () => {
    await refreshDepositRecon();
    return { ok: true };
  });

  useEffect(() => {
    function go(): void {
      const id = window.location.hash.slice(1);
      const picked = menuFromHash();
      if (picked) setMenu(picked);
      if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
    }
    go();
    window.addEventListener('hashchange', go);
    return () => window.removeEventListener('hashchange', go);
  }, []);

  let deposits: DepositRow[];
  let pettyCash: PettyCashRow[];
  let otherCashItems: OtherCashItemRow[];
  try {
    deposits = Object.values(getTable<DepositRow>('deposit'));
    pettyCash = Object.values(getTable<PettyCashRow>('pettyCash'));
    otherCashItems = Object.values(getTable<OtherCashItemRow>('otherCashItem'));
    void deposits;
  } catch (err) {
    const fault = <FaultState name="money data unreadable" detail={err instanceof Error ? err.message : String(err)} />;
    if (embedded) return fault;
    return (
      <div class="main">
        <div class="page-header"><h1>Money</h1></div>
        {fault}
      </div>
    );
  }

  // No early return on "nothing loaded" (fixed 2026-08-15). Every one of the
  // seven money tables IS empty in production right now, and this page used
  // to render one line — "No money data loaded yet." — and stop, which put
  // every add form below a return that never ran. An empty store is a real,
  // nameable state (INV-9), not a fault; each section below names its own
  // empty state and its add form mounts regardless. Only an UNREADABLE store
  // (the catch above) is a fault worth stopping the page for.

  // ONE clinic at a time, chosen from the tab strip — the live app's own shape
  // (ground truth: the deployed board, screenshotted 2026-08-15). The rebuild
  // used to open with four overview cards and then mix every clinic's rows into
  // flat tables; the real Money page has neither. Home is where the
  // four-clinic view lives, and it already does.
  const clinics = visibleClinics();
  const activeClinic = embeddedClinic ?? activeClinicFrom(clinic, clinics);
  const folders = useResource<{ folders?: Array<{ name: string; folderUrl?: string }> }>(
    `money-folders:${activeClinic}`,
    () => authedGet(`/api/v1/money/folders?location=${encodeURIComponent(activeClinic)}`),
  );
  const depositsFolderUrl = folders.status === 'ready'
    ? (folders.data.folders ?? []).find((f) => f.name === 'Deposits')?.folderUrl
    : undefined;

  const mine = <T extends { locationCode: string }>(list: T[]): T[] =>
    list.filter((r) => r.locationCode === activeClinic);

  const pettyRows = groupedByLocation(mine(pettyCash), 'transactionDate');
  const otherCashRows = groupedByLocation(mine(otherCashItems), 'itemDate');

  const pettySort = useTableSort(pettyRows, {
    entry: (p) => p.locationCode,
    direction: (p) => p.direction,
    amount: (p) => Number(p.amount),
    by: (p) => p.recordedBy,
  });
  const otherCashSort = useTableSort(otherCashRows, {
    item: (o) => o.locationCode,
    direction: (o) => o.direction,
    amount: (o) => Number(o.amount),
    by: (o) => o.recordedBy,
  });

  // Cursor review #61 (owner, 2026-10-15): the lists hold the last 90 days
  // until "Show older" loads the rest (lib/bootstrap.ts loadMoneyLists).
  async function showOlder(): Promise<void> {
    setOlderState('loading');
    try {
      await showOlderMoney();
      setOlderState('idle');
    } catch (err) {
      setOlderState(err instanceof Error ? err.message : String(err));
    }
  }
  const olderControl = moneyShowOlder.value ? null : (
    <div class="section-head-actions" data-testid="money-history-window">
      <span class="meta">Showing the last 90 days (from {formatDate(moneyHistoryFrom())}).</span>
      <button
        type="button"
        class="btn"
        data-testid="money-show-older"
        disabled={olderState === 'loading'}
        onClick={() => { void showOlder(); }}
      >
        {olderState === 'loading' ? 'Loading…' : 'Show older'}
      </button>
      {olderState !== 'idle' && olderState !== 'loading'
        ? <FaultState name="money_older_load_failed" detail={olderState} />
        : null}
    </div>
  );

  const body = (
        <>
      <div class="mgmt-menu" data-testid="money-menu">
        {MONEY_MENU.map((item) => (
          <button
            type="button"
            class="btn"
            key={item.id}
            data-testid={`money-menu-${item.id}`}
            aria-pressed={menu === item.id}
            onClick={() => setMenu(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {menu === 'deposits' ? (
      <section class="card" id="deposits">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Deposits</h2>
          <div class="section-head-actions deposit-head-actions">
            <FavoriteStar itemKey="money.deposits" />
            <OpenFolderLink url={depositsFolderUrl} label="Deposits" text="Go to folder" />
          </div>
        </div>
        <p>Enter the daily numbers, mark the dates in this set, then close the set with one MR080 total and one deposit total.</p>
      </div>
      <DepositReconGrid locationCode={activeClinic} />
      </section>
      ) : null}

      {menu === 'petty' ? (
      <section class="card" id="petty">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Petty cash</h2>
          <div class="section-head-actions">
            <FavoriteStar itemKey="money.petty" />
          </div>
        </div>
        <p>Expense entries per clinic.</p>
      </div>
      <AddPettyCashForm
        key={editingPetty ? `edit-${editingPetty.id}` : 'add'}
        editing={editingPetty}
        onDone={() => setEditingPetty(null)}
      />
      {pettyRows.length === 0 ? (
        <EmptyState label="No petty cash entries yet." />
      ) : (
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <SortableTh label="Entry" sortKey="entry" state={pettySort.state} onSort={pettySort.toggle} />
                <SortableTh label="Direction" sortKey="direction" state={pettySort.state} onSort={pettySort.toggle} />
                <SortableTh label="Amount" sortKey="amount" state={pettySort.state} onSort={pettySort.toggle} />
                <SortableTh label="Recorded by" sortKey="by" state={pettySort.state} onSort={pettySort.toggle} />
                <th />
              </tr>
            </thead>
            <tbody>
              {pettySort.rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div class="tk">{locationName(p.locationCode)}</div>
                    <div class="meta">
                      {formatDate(p.transactionDate)}
                      {p.reason ? <> &middot; {p.reason}</> : null}
                    </div>
                  </td>
                  <td>{p.direction}</td>
                  <td>{formatMoney(p.amount)}</td>
                  <td>{p.recordedBy ?? '—'}</td>
                  <td>
                    <PettyReceipts id={p.id} receipts={p.receipts ?? []} />{' '}
                    <button type="button" class="btn" onClick={() => setEditingPetty(p)}>Edit</button>
                    <DeleteMoneyRowButton entity="pettyCash" id={p.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {olderControl}

      </section>
      ) : null}

      {menu === 'other' ? (
      <section class="card" id="other">
      <div class="section-head">
        <div class="section-head-row">
          <h2>Other cash items</h2>
          <div class="section-head-actions">
            <FavoriteStar itemKey="money.other" />
          </div>
        </div>
        <p>Cash that is not a deposit or petty cash.</p>
      </div>
      <AddOtherCashItemForm
        key={editingOther ? `edit-${editingOther.id}` : 'add'}
        editing={editingOther}
        onDone={() => setEditingOther(null)}
      />
      {otherCashRows.length === 0 ? (
        <EmptyState label="No other cash items logged yet." />
      ) : (
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <SortableTh label="Item" sortKey="item" state={otherCashSort.state} onSort={otherCashSort.toggle} />
                <SortableTh label="Direction" sortKey="direction" state={otherCashSort.state} onSort={otherCashSort.toggle} />
                <SortableTh label="Amount" sortKey="amount" state={otherCashSort.state} onSort={otherCashSort.toggle} />
                <SortableTh label="Recorded by" sortKey="by" state={otherCashSort.state} onSort={otherCashSort.toggle} />
                <th />
              </tr>
            </thead>
            <tbody>
              {otherCashSort.rows.map((o) => (
                <tr key={o.id}>
                  <td>
                    <div class="tk">{locationName(o.locationCode)}</div>
                    <div class="meta">
                      {formatDate(o.itemDate)}
                      {o.description ? <> &middot; {o.description}</> : null}
                    </div>
                  </td>
                  <td>{o.direction}</td>
                  <td>{formatMoney(o.amount)}</td>
                  <td>{o.recordedBy ?? '—'}</td>
                  <td>
                    <button type="button" class="btn" onClick={() => setEditingOther(o)}>Edit</button>
                    <DeleteMoneyRowButton entity="otherCashItem" id={o.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {olderControl}

      </section>
      ) : null}
        </>
  );

  if (embedded) return <div data-testid="company-money">{body}</div>;

  return (
    <div class="main">
      <div class="page-header">
        <h1>Money</h1>
      </div>

      <ClinicTabs
        clinics={clinics}
        active={showManagementTab && management ? MANAGEMENT_TAB_ID : activeClinic}
        onSelect={(code) => { setClinic(code); setManagement(false); }}
        // Empty Management tab hidden for everyone until it has content
        // (owner, 2026-10-06 — see SHOW_EMPTY_MANAGEMENT_TABS).
        management={SHOW_EMPTY_MANAGEMENT_TABS}
        onSelectAfter={() => setManagement(true)}
      />

      {showManagementTab && management ? <ManagementPlaceholder section="Money" /> : body}
    </div>
  );
}
